import { useCallback } from "react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

// A4 at 96 DPI.
const PDF_WIDTH_PX = 794;
const PDF_HEIGHT_PX = 1123;
const PAGE_TOP_PADDING = 15;
const PAGE_BOTTOM_PADDING = 15;
const PAGE_LIMIT = PDF_HEIGHT_PX - PAGE_TOP_PADDING - PAGE_BOTTOM_PADDING; // 1093
const USABLE_BOTTOM = PDF_HEIGHT_PX - PAGE_BOTTOM_PADDING; // 1108
const EPS = 0.5; // sub-pixel rounding tolerance

interface LogicalSection {
  startY: number;
  endY: number;
}

interface SubSection {
  startY: number;
  endY: number;
}

export interface PlannedSub {
  startY: number;
  endY: number;
  drawAtPdfY: number;
}

/**
 * Reads `pdf-split-page="true"` markers and returns non-overlapping logical
 * sections (Y intervals relative to contentElement).
 *
 * Two key properties that the OLD engine lacked (RR-603 — the OLD engine dropped
 * everything above the first auto-inserted break):
 *  - a synthetic section starting at Y=0 is prepended so content ABOVE the first
 *    marker is never lost;
 *  - with NO authored markers the whole document is one section [0, totalHeight]
 *    and `subdivideSection` auto-paginates it — so we don't have to mark up every
 *    stage component.
 */
export const collectLogicalSections = (contentElement: HTMLElement): LogicalSection[] => {
  const contentTop = contentElement.getBoundingClientRect().top;
  const totalHeight = contentElement.scrollHeight;

  const markerTops = (Array.from(contentElement.querySelectorAll('[pdf-split-page="true"]')) as HTMLElement[])
    .map((m) => m.getBoundingClientRect().top - contentTop)
    .sort((a, b) => a - b);

  if (markerTops.length === 0) {
    return totalHeight > EPS ? [{ startY: 0, endY: totalHeight }] : [];
  }

  const boundaries: number[] = [];
  if (markerTops[0] > EPS) {
    boundaries.push(0); // keep the content above the first marker
  }
  boundaries.push(...markerTops);

  const sections: LogicalSection[] = [];
  for (let i = 0; i < boundaries.length; i++) {
    const startY = boundaries[i];
    const endY = i < boundaries.length - 1 ? boundaries[i + 1] : totalHeight;
    if (endY - startY > EPS) {
      sections.push({ startY, endY });
    }
  }
  return sections;
};

/**
 * Walks the DOM inside one logical section and returns sub-sections that each fit
 * an A4 page. No DOM mutation, no break-marker insertion.
 *  - `data-pdf-keep-together` blocks are atomic (never split internally);
 *  - a child taller than a page is drilled into; if drilling finds no inner break
 *    it is treated as an oversized atomic block on its own sub-page;
 *  - `bottom` is clamped to `section.endY` so a wrapper spilling past its section
 *    doesn't falsely trigger an overflow.
 */
export const subdivideSection = (section: LogicalSection, contentElement: HTMLElement): SubSection[] => {
  const contentTop = contentElement.getBoundingClientRect().top;
  const breaks: number[] = [];
  let pageStartY = section.startY;

  const walk = (element: HTMLElement, depth: number): void => {
    const children = Array.from(element.children) as HTMLElement[];
    for (const child of children) {
      const rect = child.getBoundingClientRect();
      const top = rect.top - contentTop;
      const bottom = rect.bottom - contentTop;

      if (bottom <= section.startY + EPS) continue; // entirely before this section
      if (top >= section.endY - EPS) break; // past this section — stop the loop

      const effectiveBottom = Math.min(bottom, section.endY);
      const overflows = effectiveBottom - pageStartY > PAGE_LIMIT + EPS;
      if (!overflows) continue;

      const isKeepTogether = child.hasAttribute("data-pdf-keep-together");
      const isTooTall = bottom - top > PAGE_LIMIT + EPS;
      const canDrill = !isKeepTogether && depth < 10 && child.children.length > 0;

      if (isTooTall && canDrill) {
        const before = breaks.length;
        walk(child, depth + 1);
        if (breaks.length > before) continue; // drilling found break points — done
        // else: effectively atomic — fall through to oversize handling
      }

      if (isTooTall) {
        if (top > pageStartY + EPS) breaks.push(top);
        if (bottom < section.endY - EPS) breaks.push(bottom);
        pageStartY = bottom;
        continue;
      }

      // Normal child overflowing the current page — start a new page before it.
      if (top > pageStartY + EPS) {
        breaks.push(top);
        pageStartY = top;
      }
    }
  };

  walk(contentElement, 0);

  const points = Array.from(
    new Set([
      section.startY,
      ...breaks.filter((b) => b > section.startY + EPS && b < section.endY - EPS),
      section.endY,
    ])
  ).sort((a, b) => a - b);

  const subs: SubSection[] = [];
  for (let i = 0; i < points.length - 1; i++) {
    if (points[i + 1] - points[i] > EPS) {
      subs.push({ startY: points[i], endY: points[i + 1] });
    }
  }
  return subs;
};

/**
 * Greedy A4 packing of sub-sections. Pure (numbers only) — unit-tested directly.
 * Each sub-section either fits the current page or starts a new one; a sub taller
 * than a page gets its own page (visual overflow at the A4 bottom is accepted).
 */
export const packPages = (subs: SubSection[]): PlannedSub[][] => {
  const pages: PlannedSub[][] = [];
  let current: PlannedSub[] = [];
  let cursor = PAGE_TOP_PADDING;

  const flush = () => {
    if (current.length) {
      pages.push(current);
      current = [];
      cursor = PAGE_TOP_PADDING;
    }
  };

  for (const sub of subs) {
    const height = sub.endY - sub.startY;
    if (height <= EPS) continue;

    if (height > PAGE_LIMIT + EPS) {
      flush();
      pages.push([{ startY: sub.startY, endY: sub.endY, drawAtPdfY: PAGE_TOP_PADDING }]);
      continue;
    }

    if (cursor + height <= USABLE_BOTTOM + EPS) {
      current.push({ startY: sub.startY, endY: sub.endY, drawAtPdfY: cursor });
      cursor += height;
    } else {
      flush();
      current.push({ startY: sub.startY, endY: sub.endY, drawAtPdfY: PAGE_TOP_PADDING });
      cursor = PAGE_TOP_PADDING + height;
    }
  }
  flush();
  return pages;
};

const planPages = (contentElement: HTMLElement): PlannedSub[][] => {
  const sections = collectLogicalSections(contentElement);
  const subs = sections.flatMap((s) => subdivideSection(s, contentElement));
  return packPages(subs);
};

export const useGeneratePdf = () => {
  const generatePdf = useCallback(
    async (
      contentElement: HTMLElement | null,
      onProgress?: (percent: number, label: string) => void
    ): Promise<Blob> => {
      if (!contentElement) {
        throw new Error("Content element not found");
      }

      // Remove pdf-exclude elements from DOM entirely (display:none doesn't always work with html2canvas)
      const excludedElements = contentElement.querySelectorAll("[pdf-exclude='true']");
      const excludedData: { el: Element; parent: Node; nextSibling: Node | null }[] = [];
      excludedElements.forEach((el) => {
        if (el.parentNode) {
          excludedData.push({ el, parent: el.parentNode, nextSibling: el.nextSibling });
          el.parentNode.removeChild(el);
        }
      });

      // Convert badges to plain text
      const badgeElements = contentElement.querySelectorAll("[pdf-badge='true']");
      const badgeOriginals: { parent: Node; badge: Element; placeholder: Text }[] = [];
      badgeElements.forEach((badge) => {
        const textContent = badge.textContent?.trim() || "";
        const textNode = document.createTextNode(textContent);
        if (badge.parentNode) {
          badge.parentNode.replaceChild(textNode, badge);
          badgeOriginals.push({ parent: textNode.parentNode!, badge, placeholder: textNode });
        }
      });

      // Force light theme for PDF rendering by removing .dark class
      const htmlEl = document.documentElement;
      const wasDark = htmlEl.classList.contains("dark");
      if (wasDark) {
        htmlEl.classList.remove("dark");
      }

      // Set lang for hyphenation (Polish), restored in finally.
      const previousLang = contentElement.lang;
      contentElement.lang = "pl";

      // CSS fixes for text clipping — only target text elements, not canvas/svg
      const pdfFixStyle = document.createElement("style");
      pdfFixStyle.setAttribute("data-pdf-fix", "true");
      pdfFixStyle.textContent = `
        [data-pdf-id] h1, [data-pdf-id] h2, [data-pdf-id] h3,
        [data-pdf-id] h4, [data-pdf-id] h5, [data-pdf-id] h6 {
          line-height: 1.6 !important;
          padding-bottom: 0.15em !important;
        }
        [data-pdf-id] p, [data-pdf-id] span:not([pdf-split-page]), [data-pdf-id] li,
        [data-pdf-id] a, [data-pdf-id] td, [data-pdf-id] th,
        [data-pdf-id] label {
          line-height: 1.6 !important;
          padding-bottom: 0.15em !important;
          hyphens: auto;
          -webkit-hyphens: auto;
          overflow: visible !important;
        }
        [data-pdf-id] td .truncate, [data-pdf-id] th .truncate {
          overflow: visible !important;
          white-space: normal !important;
          text-overflow: clip !important;
        }
        [data-pdf-id] [data-pdf-keep-together] {
          overflow: visible !important;
          max-height: none !important;
        }
        [data-pdf-id] [pdf-exclude="true"] {
          display: none !important;
          height: 0 !important;
          overflow: hidden !important;
        }
      `;
      document.head.appendChild(pdfFixStyle);

      // Fix for Chrome 146+: getComputedStyle() now returns lab()/oklch() colors which
      // html2canvas 1.4.1 cannot parse. Monkey-patch getComputedStyle to intercept
      // and convert unsupported color functions on the fly during html2canvas rendering.
      const originalGetComputedStyle = window.getComputedStyle;
      const labConvertCanvas = document.createElement("canvas");
      labConvertCanvas.width = 1;
      labConvertCanvas.height = 1;
      const labConvertCtx = labConvertCanvas.getContext("2d", { willReadFrequently: true });
      const unsupportedColorRe = /(?:lab|oklch|oklab|lch|color)\(/;

      const convertLabColor = (value: string): string => {
        if (!labConvertCtx || !unsupportedColorRe.test(value)) return value;
        labConvertCtx.clearRect(0, 0, 1, 1);
        labConvertCtx.fillStyle = "rgba(0,0,0,0)";
        labConvertCtx.fillStyle = value;
        labConvertCtx.fillRect(0, 0, 1, 1);
        const d = labConvertCtx.getImageData(0, 0, 1, 1).data;
        return `rgba(${d[0]},${d[1]},${d[2]},${(d[3] / 255).toFixed(3)})`;
      };

      window.getComputedStyle = function (elt: Element, pseudoElt?: string | null) {
        const original = originalGetComputedStyle.call(window, elt, pseudoElt);
        return new Proxy(original, {
          get(target, prop: string) {
            const value = target[prop as keyof CSSStyleDeclaration];
            if (typeof value === "string" && unsupportedColorRe.test(value)) {
              return convertLabColor(value);
            }
            if (typeof value === "function") {
              return value.bind(target);
            }
            return value;
          },
        });
      } as typeof window.getComputedStyle;

      try {
        onProgress?.(60, "Analiza struktury dokumentu...");

        const renderScale = 2;
        const captureHeight = Math.max(contentElement.scrollHeight, PDF_HEIGHT_PX);

        onProgress?.(65, "Generowanie obrazu strony...");
        const canvas = await html2canvas(contentElement, {
          scale: renderScale,
          useCORS: true,
          allowTaint: true,
          backgroundColor: "#ffffff",
          width: PDF_WIDTH_PX,
          height: captureHeight,
          scrollX: 0,
          scrollY: 0,
          removeContainer: false,
          foreignObjectRendering: false,
          imageTimeout: 15000,
          windowWidth: PDF_WIDTH_PX,
          windowHeight: captureHeight,
        });

        onProgress?.(80, "Składanie PDF...");

        // Plan pages in memory (no DOM mutation), then slice the single capture
        // onto fixed A4 pages per the plan.
        const planned = planPages(contentElement);
        const pages: PlannedSub[][] =
          planned.length > 0
            ? planned
            : [[{ startY: 0, endY: contentElement.scrollHeight, drawAtPdfY: PAGE_TOP_PADDING }]];

        // canvas pixels per content pixel (accounts for any html2canvas rounding).
        const scaleY = canvas.height / captureHeight;

        const pdf = new jsPDF({
          orientation: "portrait",
          unit: "px",
          format: [PDF_WIDTH_PX, PDF_HEIGHT_PX],
          // Without this, jsPDF's "px" unit uses 96/72 pt-per-px and emits an oversized
          // ~A2 page (1058x1497pt) — content then looks tiny when printed/normalised to A4.
          // px_scaling switches to 0.75 pt-per-px, so [794,1123]px = a true A4 page
          // (595x842pt). All px coordinates scale by the same factor, so nothing else changes.
          hotfixes: ["px_scaling"],
        });

        pages.forEach((page, pageIndex) => {
          if (pageIndex > 0) {
            pdf.addPage([PDF_WIDTH_PX, PDF_HEIGHT_PX], "portrait");
          }

          // White background (prevents black areas where the page has no image).
          pdf.setFillColor(255, 255, 255);
          pdf.rect(0, 0, PDF_WIDTH_PX, PDF_HEIGHT_PX, "F");

          for (const sub of page) {
            const heightPx = sub.endY - sub.startY;
            const srcY = Math.max(0, Math.floor(sub.startY * scaleY));
            const srcH = Math.min(Math.ceil(heightPx * scaleY), canvas.height - srcY);
            if (srcH <= 0) continue;

            const slice = document.createElement("canvas");
            slice.width = canvas.width;
            slice.height = srcH;
            const ctx = slice.getContext("2d");
            if (!ctx) continue;

            ctx.fillStyle = "#ffffff";
            ctx.fillRect(0, 0, slice.width, slice.height);
            ctx.drawImage(canvas, 0, srcY, canvas.width, srcH, 0, 0, slice.width, srcH);

            pdf.addImage(
              slice.toDataURL("image/jpeg", 0.85),
              "JPEG",
              0,
              sub.drawAtPdfY,
              PDF_WIDTH_PX,
              heightPx
            );
          }
        });

        onProgress?.(90, "Finalizowanie PDF...");
        return pdf.output("blob");
      } finally {
        // Remove temporary PDF fix styles
        pdfFixStyle.remove();

        // Restore original getComputedStyle
        window.getComputedStyle = originalGetComputedStyle;

        // Restore lang
        contentElement.lang = previousLang;

        // Restore dark theme if it was active
        if (wasDark) {
          htmlEl.classList.add("dark");
        }

        // Restore excluded elements to DOM (reverse order to handle nesting)
        for (let i = excludedData.length - 1; i >= 0; i--) {
          const { el, parent, nextSibling } = excludedData[i];
          try {
            if (nextSibling && nextSibling.parentNode === parent) {
              parent.insertBefore(el, nextSibling);
            } else {
              parent.appendChild(el);
            }
          } catch {
            // Element couldn't be restored, skip
          }
        }

        // Restore badges
        badgeOriginals.forEach(({ parent, badge, placeholder }) => {
          if (parent && placeholder.parentNode === parent) {
            parent.replaceChild(badge, placeholder);
          }
        });

        setTimeout(() => {
          document.body.style.pointerEvents = "";
          document.documentElement.style.pointerEvents = "";
        }, 100);
      }
    },
    []
  );

  return { generatePdf };
};
